import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";

/**
 * LiveBrowsingTicker — "🟢 X campers browsing right now" pill.
 *
 *   • Pings POST /presence/ping every 30 s while the tab is open. The
 *     ping itself returns the latest count so we get the data without
 *     a separate GET.
 *   • Hides itself gracefully on error (server down, offline, etc.) —
 *     never blocks the page.
 *   • Pauses pings when the tab is hidden so background tabs don't
 *     inflate the count or burn battery.
 *   • Animates the number on change (subtle up/down tick) so the
 *     ticker feels alive, not static.
 */
export default function LiveBrowsingTicker({ className = "", siteId = null, compact = false }) {
  const [count, setCount] = useState(null);
  const [cities, setCities] = useState([]);
  const [pulse, setPulse] = useState(false);
  const lastRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      if (document.hidden) return;
      try {
        // Site-scoped ticker pings with ?site_id=... and reads from a
        // smaller endpoint; the global ticker uses the standard ping.
        const url = siteId
          ? `/presence/ping?site_id=${encodeURIComponent(siteId)}`
          : "/presence/ping";
        const { data } = await api.post(url);
        if (cancelled) return;
        const n = siteId
          // For site pages, hit the scoped count after the ping registers us.
          ? await api.get(`/presence/site/${encodeURIComponent(siteId)}`)
              .then((r) => Number(r?.data?.active || 0))
              .catch(() => Number(data?.active || 0))
          : Number(data?.active || 0);
        if (cancelled) return;
        // Always update so the count can fall back down (was stuck at the
        // last positive value before — bug where leftover viewers from
        // hours ago appeared "stuck" until the page refreshed).
        if (lastRef.current != null && lastRef.current !== n) {
          setPulse(true);
          setTimeout(() => !cancelled && setPulse(false), 700);
        }
        lastRef.current = n;
        setCount(n);
        // Top cities only relevant for the global ticker
        if (!siteId && Array.isArray(data?.top_cities)) {
          setCities(data.top_cities.slice(0, 3));
        }
      } catch {
        // silent — ticker just stops rendering on next state check
      }
    };

    // first ping immediately, then every 30s
    tick();
    timerRef.current = setInterval(tick, 30000);
    // ping again the moment the tab is refocused
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);
    // External "bump" event — fired by other components (e.g. the poll on
    // vote) so the ticker visibly responds to user actions rather than
    // waiting for the next 30s tick.
    const onBump = () => tick();
    window.addEventListener("cc:presence-bump", onBump);
    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("cc:presence-bump", onBump);
    };
  }, [siteId]);

  // iter94 — only render the live counter once enough campers are present
  // to feel like a community. UK CPR-safe: hiding low counts is fine,
  // only inflating numbers would be illegal. 1-2 viewers looks lonely
  // and hurts social proof — 6+ feels alive.
  const MIN_TO_SHOW = 6;
  if (count == null || count < MIN_TO_SHOW) return null;

  // Site-scoped compact rendering — tighter pill for the detail page.
  if (siteId) {
    return (
      <div
        className={`inline-flex items-center gap-2 px-2.5 py-1 border hairline bg-white text-[#111] ${className}`}
        data-testid="live-site-viewers"
        aria-live="polite"
        aria-label={`${count} ${count === 1 ? "camper" : "campers"} viewing this site`}
      >
        <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70 animate-ping" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
        </span>
        <span className={`mono text-[11px] font-bold tabular-nums transition-transform duration-500 ${pulse ? "scale-110 text-emerald-600" : ""}`} data-testid="live-site-viewers-count">
          {count.toLocaleString("en-GB")}
        </span>
        <span className="text-[10px] uppercase tracking-[1.5px] text-[#666]">
          {count === 1 ? "viewing now" : "viewing now"}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`inline-flex flex-col items-end gap-1 px-3 py-1.5 border hairline-strong bg-white/95 backdrop-blur-sm ${className}`}
      data-testid="live-browsing-ticker"
      aria-live="polite"
      aria-label={`${count} campers browsing right now${cities.length ? `, from ${cities.join(", ")}` : ""}`}
    >
      <div className="inline-flex items-center gap-2">
        <span className="relative flex h-2 w-2" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <span className="text-[10px] font-bold uppercase tracking-[1.5px] text-[#888]">
          Browsing now
        </span>
        <span
          className={`mono text-sm font-bold text-[#111] tabular-nums transition-transform duration-500 ${pulse ? "scale-110 text-emerald-600" : "scale-100"}`}
          data-testid="live-browsing-count"
        >
          {count.toLocaleString("en-GB")}
        </span>
      </div>
      {cities.length > 0 && !compact && (
        <div
          className="text-[9.5px] mono tracking-wider text-[#888] uppercase truncate max-w-[180px]"
          data-testid="live-browsing-cities"
          title={`Top cities: ${cities.join(", ")}`}
        >
          from {cities.join(" · ")}
        </div>
      )}
    </div>
  );
}
